import React from 'react';
import { Responsive as ResponsiveGridLayout, Layout } from 'react-grid-layout';
import {
  Box,
  Paper,
  IconButton,
  GlobalStyles,
  Skeleton,
  ListItemButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import PanelSelector from '../PanelSelector/PanelSelector';
import { IAnyPanel } from '../Dashboard.model';
import { useMeasure } from 'react-use';

interface DashboardViewProps {
  panels: IAnyPanel[];
  layout: Layout | undefined;
  isEditMode: boolean;
  rowHeight: number;
  xsColumns: number;
  theme: any;
  isLoadingLayout: boolean;
  onRemovePanel: (id: string) => void;
  onLayoutChange: (layout: Layout) => void;
  handleResize?: any;
  header: React.ReactNode;
}

const DashboardView: React.FC<DashboardViewProps> = ({
  panels,
  layout,
  isEditMode,
  rowHeight,
  xsColumns,
  theme,
  isLoadingLayout,
  onRemovePanel,
  onLayoutChange,
  handleResize,
  header,
}) => {
  const [ref, { width }] = useMeasure();

  return (
    <Box sx={{ px: 1, width: '100%' }} ref={ref}>
      <GlobalStyles
        styles={{
          '.dashboard-layout .react-grid-item > .react-resizable-handle::after':
            {
              right: '5px',
              bottom: '5px',
              width: '10px',
              height: '10px',
              borderRight: `3px solid ${theme.palette.primary.main}`,
              borderBottom: `3px solid ${theme.palette.primary.main}`,
            },
        }}
      />
      {header}
      {isLoadingLayout && !layout ? (
        <Skeleton
          variant="rectangular"
          width="100%"
          height={400}
          animation="wave"
        />
      ) : (
        <ResponsiveGridLayout
          className="dashboard-layout"
          layouts={{
            lg: layout,
            md: layout,
            sm: layout,
            xs: layout,
            xxs: layout,
          }}
          cols={{
            lg: xsColumns,
            md: xsColumns,
            sm: xsColumns,
            xs: xsColumns,
            xxs: 1,
          }}
          rowHeight={rowHeight}
          width={width || 815}
          onLayoutChange={onLayoutChange}
          onResize={handleResize}
        >
          {panels.map((panel) => (
            <div key={panel.id}>
              <PanelButton
                key={panel.id}
                panel={panel}
                layout={layout}
                isEditMode={isEditMode}
                theme={theme}
                onRemovePanel={onRemovePanel}
              />
            </div>
          ))}
        </ResponsiveGridLayout>
      )}
      {/* <p>State: {isEditMode ? 'Edit Mode' : 'View Mode'}</p>
      <p>Layout: {layout ? JSON.stringify(layout) : 'No layout defined'}</p>
      <p>Panels: {JSON.stringify(panels)}</p> */}
    </Box>
  );
};

interface PanelButtonProps {
  panel: IAnyPanel;
  layout: Layout | undefined;
  isEditMode: boolean;
  theme: any;
  onRemovePanel: (id: string) => void;
}
function PanelButton(props: PanelButtonProps) {
  const { panel, layout, isEditMode, theme, onRemovePanel } = props;
  const content = (
    <Paper
      elevation={1}
      sx={[
        {
          p: 1,
          height: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative',
          borderRadius: '18px',
        },
        isEditMode
          ? { border: `2px dashed ${theme.palette.primary.main}` }
          : { border: 'none' },
      ]}
    >
      {isEditMode && (
        <IconButton
          size="small"
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            bgcolor: 'error.main',
            color: 'white',
            '&:hover': { bgcolor: 'error.dark' },
          }}
          onClick={() => onRemovePanel(panel.id)}
        >
          <Close fontSize="small" />
        </IconButton>
      )}
      <PanelSelector
        panel={panel}
        layout={layout?.find((l) => l.i === panel.id)}
      />
    </Paper>
  );
  if (panel.href && !isEditMode && false) {
    return (
      <ListItemButton
        disableGutters
        disableRipple={!panel.href || isEditMode}
        disableTouchRipple={!panel.href || isEditMode}
        {...(panel.href && !isEditMode
          ? { component: undefined, href: panel.href }
          : {})}
        sx={[
          {
            height: '100%',
            width: '100%',
            borderRadius: '18px',
            p: 0,
            cursor: panel.href && !isEditMode ? 'pointer' : 'initial',
          },
        ]}
      >
        {content}
      </ListItemButton>
    );
  } else {
    return content;
  }
}

export default DashboardView;
